import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React from "react";
import useDataList from '../../hooks/useDataList';
import { Loading } from '@widget/loaders';
import NotificationBlock from '@helper/NotificationBlock';
import classNames from 'classnames';
import { UXPContext } from '@core/context/Context';
import Icon from "@icon/Icon";
import ButtonComponent from "@widget/buttons/ButtonComponent";
/**
 *
 * A infinite-scrollable list that supports paging in of items
 * @export
 *
 * @example
 * ```
 *  <DataTable
 *      data={(max, last) => getDataItems(max, last)}
 *      pageSize={10}
 *      columns={[
 *          {
 *              title: "Request",
 *              width: "30%",
 *              renderColumn: (item) => <ItemCard
 *                  item={item}
 *                  subTitleField="request"
 *                  className="data-table-item"
 *              />
 *          },
 *          {
 *              title: "User",
 *              width: "25%",
 *              renderColumn: (item) => <ItemCard
 *                  item={item}
 *                  subTitleField="user"
 *                  className="data-table-item"
 *              />
 *          },
 *          {
 *              title: "Zone",
 *              width: "15%",
 *              renderColumn: (item) => <ItemCard
 *                  item={item}
 *                  subTitleField="section"
 *                  className="data-table-item"
 *              />
 *          },
 *          {
 *              title: "Status",
 *              width: "15%",
 *              renderColumn: (item) => <ItemCard
 *                  item={item}
 *                  subTitleField="status"
 *                  className="data-table-item"
 *              />
 *          },
 *          {
 *              title: "Date",
 *              width: "15%",
 *              renderColumn: (item) => <ItemCard
 *                  item={item}
 *                  subTitleField="date"
 *                  className="data-table-item"
 *              />
 *          }
 *      ]}
 *  />
 * ```
 *
 * @deprecated Use ObjectSearchComponent (`paging="infinite"` for scrolling lists), or SimpleConfigurationTable for a
 *   short static table. DataTable keeps working; it gets no new features. See docs/v5/deprecated-components.md.
 * @category Data Display/Tables
 */
const DataTable = (props) => {
    const context = React.useContext(UXPContext);
    let { data, pageSize, columns, renderLoading, className, args, showFooter = true, scrollStep = 5, showEndOfContent = true, onItemsLoad, renderHeaders = true, onClickRow, activeClass = 'active' } = props;
    let [currentKey, setCurrentKey] = React.useState(0);
    let [showUp, setShowUp] = React.useState(false);
    let [showDown, setShowDown] = React.useState(false);
    let [showGoUp, setShowGoUp] = React.useState(false);
    // search list params 
    let [param, setParams] = React.useState({
        page: 0,
        lastPageToken: null,
        args: args
    });
    // ref 
    let lastPageToken = React.useRef(null);
    let timer = null;
    // if arguments has changed 
    //reset page count & page token
    React.useEffect(() => {
        lastPageToken.current = null;
        setParams({
            page: 0,
            lastPageToken: null,
            args: args
        });
    }, [args, data]);
    // use data list
    let { items, pageToken, hasMore, loading, error, total } = useDataList(data, pageSize, param.page, param.lastPageToken, param.args);
    React.useEffect(() => {
        let _total = total;
        if (!_total)
            _total = items.length;
        if (onItemsLoad) {
            onItemsLoad(_total, items.length);
        }
    }, [total, items]);
    // update last page token
    React.useEffect(() => { lastPageToken.current = pageToken; }, [pageToken]);
    // refs
    const observer = React.useRef(null);
    const listContainerRef = React.useRef(null);
    const listRef = React.useRef(null);
    const listItemsRef = React.useRef([]);
    // last ref 
    const lastItemRef = React.useCallback((node) => {
        if (loading)
            return;
        if (observer.current)
            observer.current.disconnect();
        observer.current = new IntersectionObserver((entries) => {
            if (entries[0].isIntersecting && hasMore) {
                setParams(prev => {
                    let updated = Object.assign({}, prev);
                    updated.page = updated.page + 1;
                    updated.lastPageToken = lastPageToken.current;
                    // fallback 
                    if (updated.page > 0 && updated.lastPageToken == null)
                        updated.lastPageToken = items.length.toString();
                    return updated;
                });
            }
        });
        if (node)
            observer.current.observe(node);
    }, [loading, hasMore]);
    React.useEffect(() => {
        toggleFooter();
    }, [currentKey]);
    // toggle footer 
    const toggleFooter = () => {
        let _showUp = false;
        let _showDown = true;
        let _showGoUP = false;
        if (currentKey > 1)
            _showUp = true;
        if (!hasMore && currentKey > 0 && currentKey >= items.length - 3 && currentKey <= items.length - 1) {
            // _showDown = false
            _showGoUP = true;
        }
        setShowUp(_showUp);
        setShowDown(_showDown);
        setShowGoUp(_showGoUP);
    };
    // scroll
    const scrollItemToView = (key) => {
        let scrollTop = 0;
        items.map((item, index) => {
            if (index < key) {
                let nextItemDetails = listItemsRef.current[index].getBoundingClientRect();
                scrollTop += nextItemDetails.height;
            }
        });
        listRef.current.scrollTo({
            top: scrollTop,
            behavior: 'smooth'
        });
    };
    // scroll on click
    const scrollOnclick = (direction) => {
        let _currentKey = currentKey;
        if (direction == "up") {
            _currentKey -= scrollStep;
            if (_currentKey < 0)
                _currentKey = 0;
        }
        else {
            _currentKey += scrollStep;
            if (_currentKey >= (items.length - 1)) {
                _currentKey = (items.length - 1);
                if (hasMore)
                    _currentKey = (items.length - 2);
            }
        }
        setCurrentKey(_currentKey);
        scrollItemToView(_currentKey);
    };
    // on scroll handler
    const onScrollHandler = () => {
        if (timer) {
            clearTimeout(timer);
        }
        timer = setTimeout(() => {
            let listContainerDetails = listContainerRef.current.getBoundingClientRect();
            items.map((item, key) => {
                if (!(hasMore && items.length - 1 === key)) {
                    let element = listItemsRef.current[key];
                    let boundDetails = element.getBoundingClientRect();
                    if (boundDetails.top >= listContainerDetails.top + (boundDetails.height * 0.5) && boundDetails.top <= listContainerDetails.top + (boundDetails.height * 1.5)) {
                        setCurrentKey(key);
                    }
                }
            });
        }, 200);
    };
    const handleRowClick = (e, item) => {
        let element = e.target;
        let table = element.closest('.data-table');
        console.log('active class', activeClass);
        table.querySelectorAll('tr').forEach((tr) => {
            tr.classList.remove(activeClass);
        });
        element.closest('tr').classList.add(activeClass);
        if (onClickRow) {
            onClickRow(item);
        }
    };
    //return
    return (_jsxs("div", { className: classNames("uxpcore_datatable__container data-list-container", className, { "has-footer": showFooter }), ref: listContainerRef, onScroll: onScrollHandler, children: [_jsxs("div", { className: "uxpcore_datatable__list data-list", ref: listRef, children: [_jsxs("table", { className: "uxpcore_datatable data-table", children: [renderHeaders &&
                                _jsx("thead", { children: _jsx("tr", { children: columns.map((column, key) => {
                                            let width = column.width;
                                            let styles = {};
                                            if (width)
                                                styles = { width: width };
                                            return _jsx("th", { style: styles, children: typeof column.title == "string" ? column.title : column.title() }, key);
                                        }) }) }), _jsx("tbody", { children: items.map((item, key) => {
                                    let itemRef = (el) => (listItemsRef.current[key] = el);
                                    if (key == (items.length - 1) && hasMore)
                                        itemRef = lastItemRef;
                                    return (_jsx("tr", { className: "uxpcore_datatable__row data-list-item", ref: itemRef, onClick: (e) => handleRowClick(e, item), children: columns.map((column, index) => {
                                            return _jsxs("td", { children: [" ", column.renderColumn(item)] }, index);
                                        }) }, key));
                                }) })] }), items.length == 0 && _jsx(NotificationBlock, { message: context.$L('uxp-core.text.no-items-found'), class: "uxpcore_notification--no-items-found" }), loading && _jsxs("div", { className: "uxpcore_datatable__loading dl-loading-block", children: [" ", renderLoading ? renderLoading() : _jsx(Loading, {}), " "] }), !hasMore && showEndOfContent && items.length > 0 && _jsx(NotificationBlock, { message: `-- ${context.$L('uxp-core.text.end-of-content')} --`, class: "uxpcore_notification--end-of-content" })] }), showFooter &&
                _jsxs("div", { className: classNames("uxpcore_datatable__footer data-list-footer", { "has-both": showDown && showUp }), children: [showUp &&
                            _jsx("div", { className: "uxpcore_datatable__scroll-btn uxpcore_datatable__scroll-btn--up scroll-btn scroll-up", onClick: () => scrollOnclick("up"), children: _jsx(Icon, { icon: ['fas', 'chevron-up'] }) }), showDown &&
                            _jsx("div", { className: "uxpcore_datatable__scroll-btn uxpcore_datatable__scroll-btn--down scroll-btn scroll-down", onClick: () => scrollOnclick("down"), children: _jsx(Icon, { icon: ['fas', 'chevron-down'] }) }), showGoUp &&
                            _jsx("div", { className: "uxpcore_datatable__go-up-btn-container go-up-btn-container", children: _jsx(ButtonComponent, { title: context.$L('uxp-core.text.back-to-top'), onClick: () => {
                                        setCurrentKey(0);
                                        scrollItemToView(0);
                                    }, variant: 'secondary' }) })] })] }));
};
export default DataTable;
